import React, { useState, useEffect, useRef } from 'react';
import { STANDARDIZED_COLOR_PALETTE } from 'constants/colors';
import CheckmarkIcon from 'components/Icons/CheckmarkIcon';
import { TAG_MANAGER_STRINGS, formatString } from '../../constants';
import { isSelectKey, isArrowKey } from '../../helpers/keyboardUtils';

interface TagColorPickerContentProps {
  value: string;
  onColorSelect: (color: string) => void;
}

const getNextIndex = (
  currentIndex: number,
  key: string,
  totalButtons: number
): number => {
  switch (key) {
    case TAG_MANAGER_STRINGS.ARROW_RIGHT:
      return (currentIndex + 1) % totalButtons;
    case TAG_MANAGER_STRINGS.ARROW_LEFT:
      return currentIndex === 0 ? totalButtons - 1 : currentIndex - 1;
    case TAG_MANAGER_STRINGS.ARROW_DOWN:
      return currentIndex + 4 < totalButtons ? currentIndex + 4 : currentIndex;
    case TAG_MANAGER_STRINGS.ARROW_UP:
      return currentIndex - 4 >= 0 ? currentIndex - 4 : currentIndex;
    default:
      return currentIndex;
  }
};

const handleArrowNavigation = (
  event: React.KeyboardEvent<HTMLButtonElement>,
  gridRef: React.RefObject<HTMLDivElement | null>
): void => {
  event.preventDefault();

  if (!gridRef.current) return;

  const allButtons = Array.from(
    gridRef.current.querySelectorAll<HTMLButtonElement>(
      `[data-testid^="${TAG_MANAGER_STRINGS.TEST_ID_COLOR_OPTION_PREFIX}"]`
    )
  );

  const currentIndex = allButtons.indexOf(
    event.currentTarget as HTMLButtonElement
  );
  const nextIndex = getNextIndex(currentIndex, event.key, allButtons.length);

  if (nextIndex !== currentIndex && allButtons[nextIndex]) {
    allButtons[nextIndex].focus();
  }
};

const TagColorPickerContent: React.FC<TagColorPickerContentProps> = ({
  value,
  onColorSelect
}) => {
  const [hoveredColor, setHoveredColor] = useState<string>();
  const [focusedColor, setFocusedColor] = useState<string>();
  const gridRef = useRef<HTMLDivElement>(null);

  const displayCheckmark = (color: string) =>
    value === color || hoveredColor === color || focusedColor === color;

  // Focus the selected color button or first button when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (gridRef.current) {
        const selectedButton = gridRef.current.querySelector<HTMLButtonElement>(
          `[data-testid="${TAG_MANAGER_STRINGS.TEST_ID_COLOR_OPTION_PREFIX}${value}"]`
        );

        const firstButton = gridRef.current.querySelector<HTMLButtonElement>(
          `[data-testid^="${TAG_MANAGER_STRINGS.TEST_ID_COLOR_OPTION_PREFIX}"]`
        );

        const buttonToFocus = selectedButton || firstButton;
        if (buttonToFocus) {
          buttonToFocus.focus();
        }
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div
      ref={gridRef}
      className="p-1"
      role="grid"
      tabIndex={-1}
      onClick={e => {
        // Prevent clicks inside the popover from propagating
        e.stopPropagation();
      }}
      onKeyDown={e => {
        // Handle any grid-level keyboard events if needed
        e.stopPropagation();
      }}
    >
      <div className="grid grid-cols-4 gap-1">
        {Object.entries(STANDARDIZED_COLOR_PALETTE).map(
          ([colorName, colorData]) => (
            <button
              key={colorName}
              type="button"
              data-testid={`${TAG_MANAGER_STRINGS.TEST_ID_COLOR_OPTION_PREFIX}${colorData.primary}`}
              className="h-6 w-6 rounded-sm relative flex items-center justify-center cursor-pointer border-0 p-0"
              style={{ background: colorData.primary }}
              onClick={e => {
                e.stopPropagation();
                onColorSelect(colorData.primary);
              }}
              onKeyDown={e => {
                if (isArrowKey(e.key)) {
                  handleArrowNavigation(e, gridRef);
                } else if (isSelectKey(e.key)) {
                  e.preventDefault();
                  onColorSelect(colorData.primary);
                }
              }}
              onMouseEnter={() => setHoveredColor(colorData.primary)}
              onMouseLeave={() => setHoveredColor(undefined)}
              onFocus={() => setFocusedColor(colorData.primary)}
              onBlur={() => setFocusedColor(undefined)}
              aria-label={formatString(TAG_MANAGER_STRINGS.SELECT_COLOR, {
                colorName
              })}
              aria-pressed={value === colorData.primary}
            >
              {displayCheckmark(colorData.primary) && (
                <CheckmarkIcon
                  data-testid="checkmark-icon"
                  color={colorData.text}
                />
              )}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default TagColorPickerContent;
